import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const LinqTab = ({ state }) => {
  const { students, setSqlOutput, setCsharpOutput } = state;
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('contains');
  const [orderBy, setOrderBy] = useState('name');
  const [results, setResults] = useState([]);

  const executeQuery = () => {
    let filtered = [...students];

    // Apply filter
    if (searchTerm) {
      filtered = filtered.filter(student => {
        const searchValue = searchTerm.toLowerCase();
        switch (filterType) {
          case 'contains':
            return student.name.toLowerCase().includes(searchValue);
          case 'startsWith':
            return student.name.toLowerCase().startsWith(searchValue);
          case 'endsWith':
            return student.name.toLowerCase().endsWith(searchValue);
          default:
            return true;
        }
      });
    }

    // Apply ordering
    filtered.sort((a, b) => {
      if (orderBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (orderBy === 'email') {
        return a.email.localeCompare(b.email);
      } else if (orderBy === 'date') {
        return new Date(a.enrollmentDate) - new Date(b.enrollmentDate);
      }
      return 0;
    });

    setResults(filtered);

    // Generate SQL
    let sql = 'SELECT * FROM Students';
    if (searchTerm) {
      const operator = filterType === 'contains' ? 'LIKE' : 
                       filterType === 'startsWith' ? 'LIKE' : 'LIKE';
      const pattern = filterType === 'contains' ? `'%${searchTerm}%'` :
                      filterType === 'startsWith' ? `'${searchTerm}%'` : `'%${searchTerm}'`;
      sql += `\nWHERE Name ${operator} ${pattern}`;
    }
    
    const orderColumn = orderBy === 'name' ? 'Name' : 
                       orderBy === 'email' ? 'Email' : 'EnrollmentDate';
    sql += `\nORDER BY ${orderColumn};`;
    
    setSqlOutput(sql);
    toast.success(`Query executed: ${filtered.length} results found`);
    const linqWhere =
        !searchTerm
            ? ""
            : filterType === "contains"
                ? `.Where(s => s.Name.Contains("${searchTerm}"))`
                : filterType === "startsWith"
                    ? `.Where(s => s.Name.StartsWith("${searchTerm}"))`
                    : `.Where(s => s.Name.EndsWith("${searchTerm}"))`;

    const linqOrder =
        orderBy === "name"
            ? ".OrderBy(s => s.Name)"
            : orderBy === "email"
                ? ".OrderBy(s => s.Email)"
                : ".OrderBy(s => s.EnrollmentDate)";

    setCsharpOutput(
        `// LINQ\nvar results = await context.Students\n` +
        `  ${linqWhere ? linqWhere : ""}\n` +
        `  ${linqOrder}\n` +
        `  .AsNoTracking()\n` +
        `  .ToListAsync();`
    );
  };

  const clearQuery = () => {
    setSearchTerm('');
    setResults([]);
    setSqlOutput('-- SQL queries will appear here');
    setCsharpOutput("// C# example will appear here");
  };

  return (
    <div className="space-y-6 pt-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold">LINQ Query Builder</h3>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>• Build type-safe queries with C# LINQ</li>
          <li>• Filter, order, and project data</li>
          <li>• EF Core translates to optimized SQL</li>
        </ul>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="search-term">Search Term</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="search-term"
              className="pl-9"
              placeholder="Enter student name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="filter-type">Filter Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger id="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contains">Contains</SelectItem>
                <SelectItem value="startsWith">Starts With</SelectItem>
                <SelectItem value="endsWith">Ends With</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="order-by">Order By</Label>
            <Select value={orderBy} onValueChange={setOrderBy}>
              <SelectTrigger id="order-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="date">Enrollment Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button className="flex-1" onClick={executeQuery}>
            <Filter className="mr-2 h-4 w-4" />
            Execute Query
          </Button>
          <Button variant="outline" onClick={clearQuery}>
            Clear
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-2"
        >
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Query Results</h4>
            <Badge>{results.length} found</Badge>
          </div>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {results.map((student, index) => (
              <motion.div
                key={student.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="rounded-lg border border-border bg-surface-elevated p-3"
              >
                <p className="font-medium">{student.name}</p>
                <p className="text-xs text-muted-foreground">{student.email}</p>
                <div className="mt-1 flex gap-2">
                  <Badge variant="outline" className="text-xs">
                    {student.enrollmentDate}
                  </Badge>
                  <Badge variant={student.status === 'Active' ? 'default' : 'secondary'} className="text-xs">
                    {student.status}
                  </Badge>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LinqTab;